import { useMutation, useQuery } from '@apollo/client';
import {
  Anchor,
  Button,
  Group,
  NumberInput,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useState } from 'react';
import { ordersDocument } from './features/order/graphql/queries';
import { OrdersQuery, OrderStatus, PaymentStatus, SortOrder } from './__generated__/graphql';
import { DataTable } from 'mantine-datatable';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { issueInvoicesDocument } from './features/order/graphql/mutations';
import { modals } from '@mantine/modals';

const VAT_RATE = 0.23;

// The orders resolver keeps its 500-row cap for every caller except this one:
// a negative `take` opts the invoice page out, so a full date range loads at once.
const NO_LIMIT = -1;

const calculateVAT = (amountWithVAT: number, vatRate: number = VAT_RATE) => {
  const baseAmount = amountWithVAT / (1 + vatRate);
  const vatAmount = amountWithVAT - baseAmount;
  return vatAmount;
};

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const InvoicePage = () => {
  const [date, setDate] = useState<[Date | null, Date | null]>([null, null]);
  const [targetVat, setTargetVat] = useState<string | number>('');
  const [selected, setSelected] = useState<OrdersQuery['orders']>([]);

  const { data, loading } = useQuery(ordersDocument, {
    variables: {
      take: NO_LIMIT,
      where: {
        invoice: { is: null },
        createdAt: { gte: date[0], lte: date[1] },
        status: { equals: OrderStatus.Shipped },
        paymentMethod: { is: { name: { in: ['Prevod', 'Dobierka'] } } },
        payments: { some: {}, every: { status: { equals: PaymentStatus.Completed } } },
        OR: [
          {
            paymentMethod: { is: { name: { equals: 'Prevod' } } },
            customer: {
              is: {
                metadata: { path: ['ibanType'], equals: 'F' },
              },
            },
          },
          {
            paymentMethod: { is: { name: { equals: 'Dobierka' } } },
          },
        ],
      },
      orderBy: {
        createdAt: SortOrder.Asc,
      },
    },
    skip: date[0] === null || date[1] === null,
    notifyOnNetworkStatusChange: true,
  });

  const { data: invoicesData } = useQuery(ordersDocument, {
    variables: {
      take: NO_LIMIT,
      where: {
        invoice: { isNot: null },
        createdAt: { gte: date[0], lte: date[1] },
      },
      orderBy: {
        createdAt: SortOrder.Asc,
      },
    },
    skip: date[0] === null || date[1] === null,
  });

  const [mutate, mutateData] = useMutation(issueInvoicesDocument, {
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
  });

  const issued = invoicesData?.orders.reduce((acc, order) => acc + order.total, 0) ?? 0;
  const issuedVat = calculateVAT(issued);
  const total = data?.orders.reduce((acc, order) => acc + order.total, 0) ?? 0;
  const vat = calculateVAT(total);
  const selectedTotal = selected.reduce((acc, order) => acc + order.total, 0);
  const selectedVat = calculateVAT(selectedTotal);

  return (
    <Stack>
      <Group justify="space-between">
        <DatePickerInput type="range" label="Pick dates range" value={date} onChange={setDate} />
        <Group>
          <NumberInput
            label="Target VAT"
            value={targetVat}
            onChange={setTargetVat}
            allowDecimal={false}
          />
          <Button
            onClick={() => {
              if (!data?.orders?.length || !targetVat) return;

              const maxTotal = Number(targetVat) * (1 / VAT_RATE + 1);
              let selectedOrders: OrdersQuery['orders'] = [];
              let currentTotal = 0;
              const selectedCustomers = new Set<string>(); // Track selected customer IDs

              // Properly shuffle orders without mutating data.orders
              const shuffledOrders = shuffleArray(data.orders);

              for (const order of shuffledOrders) {
                // If this customer was already selected, add all their orders
                if (selectedCustomers.has(order.customer.id)) {
                  continue; // Skip adding the same customer multiple times
                }

                // Mark customer as selected
                selectedCustomers.add(order.customer.id);

                // Select all orders for this customer
                const customerOrders = data.orders.filter(
                  (o) => o.customer.id === order.customer.id
                );
                for (const custOrder of customerOrders) {
                  selectedOrders.push(custOrder);
                  currentTotal += custOrder.total;
                }

                // Stop when we get close to maxTotal
                if (currentTotal >= maxTotal) break;
              }

              // Sort the selected orders by createdAt in ascending order
              selectedOrders = [...selectedOrders].sort(
                (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
              );

              setSelected(selectedOrders);
            }}
          >
            Select random
          </Button>
        </Group>
        <Stack gap={0}>
          <Text>Fakturované: {issued} €</Text>
          <Text>z toho DPH: {issuedVat} €</Text>
        </Stack>
        <Stack gap={0}>
          <Text>Spolu na fakturáciu: {total} €</Text>
          <Text>z toho DPH: {vat} €</Text>
        </Stack>
        <Stack gap={0}>
          <Text>Vybrané: {selectedTotal} €</Text>
          <Text>z toho DPH: {selectedVat} €</Text>
        </Stack>
        <Button
          disabled={selected.length === 0}
          loading={mutateData.loading}
          onClick={() =>
            modals.openConfirmModal({
              title: `Issue invoices for ${selected.length} orders?`,
              children: (
                <Stack>
                  <Text>
                    Total: ${selectedTotal} €, VAT: ${selectedVat} €
                  </Text>
                  <Stack h={250} component={ScrollArea} gap="xs">
                    {selected.map((order) => (
                      <Group key={order.id}>
                        <Text>{order.package}</Text>
                        <Text>{order.customer.name}</Text>
                        <Text>{order.total} €</Text>
                        <Text>{dayjs(order.createdAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              ),
              labels: { cancel: 'Cancel', confirm: 'Issue invoices' },
              onConfirm: async () => {
                await mutate({
                  variables: {
                    orderIds: selected.map((order) => order.id),
                  },
                });
              },
            })
          }
        >
          Issue invoices ({selected.length})
        </Button>
      </Group>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        records={data?.orders}
        borderRadius="sm"
        minHeight={200}
        fetching={loading}
        selectedRecords={selected}
        onSelectedRecordsChange={setSelected}
        columns={[
          {
            accessor: 'package',
            title: '#',
            render: ({ id, package: packageNumber }) => (
              <Anchor inherit to={`/order/${id}`} component={Link}>
                {packageNumber}
              </Anchor>
            ),
            sortable: true,
          },
          {
            accessor: 'customer.name',
            title: 'Zákazník',
            sortable: true,
          },
          {
            accessor: 'total',
            title: 'Spolu',
            sortable: true,
            render: ({ total }) => `${total} €`,
          },
          {
            accessor: 'shippingMethod.name',
            title: 'Preprava',
            sortable: true,
          },
          {
            accessor: 'paymentMethod.name',
            title: 'Platba',
            render: ({ paymentMethod, customer }) =>
              `${paymentMethod?.name ?? ''}${customer.metadata?.ibanType ? ` (${customer.metadata?.ibanType})` : ''}`,
            sortable: true,
          },
          {
            accessor: 'payments',
            title: 'Stav',
            render: ({ payments }) =>
              payments.map((payment) => (
                <Tooltip label={payment.metadata} key={payment.id}>
                  <Text>{payment.method}</Text>
                </Tooltip>
              )),
          },
          {
            accessor: 'createdAt',
            title: 'Vytvorená',
            render: ({ createdAt }) => dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss'),
            sortable: true,
          },
        ]}
      />
    </Stack>
  );
};
